import React, { useState, useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { Report } from 'graphql-api';
import { getUserEmail } from 'utils/auth';
import { getReport, uploadInputFile } from 'api/reports';
import { getHRISEmployeesBlobData } from 'api/hris-data';
import HRISDataInsights from 'components/import-data-page/hris-data-insights';
import withReport from 'components/with-report';
import ProcessingModal from 'components/processing-modal';
import Spinner from 'components/spinner';
import { navigateAndStoreNextStep } from 'utils/navigation';
import { DATA_IMPORT_STEP } from 'constants/report-steps';

function ImportingHRISDataPage({
  report,
  setReport
}: {
  report: Report;
  setReport: Function;
}): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [showHRISDataInsights, setShowHRISDataInsights] = useState<boolean>(false);

  const importHRISData = async (): Promise<void> => {
    const blobData = await getHRISEmployeesBlobData(report?.id);
    if (blobData != null) {
      const inputFile = new File([blobData], 'input-file.csv');
      const updatedReport: Report | null = await uploadInputFile(report, inputFile);
      if (updatedReport != null) {
        setReport(updatedReport);
        setIsLoading(false);
        if (report.hrisDataResynced === true) {
          await navigateAndStoreNextStep(updatedReport, DATA_IMPORT_STEP, ((
            pathname: string
          ): void => {
            navigate(pathname, { state: { refreshData: true } });
          }) as NavigateFunction);
        } else {
          setShowHRISDataInsights(true);
        }
      } else {
        setIsLoading(false);
        alert(
          'Sorry there was an issue importing data from your HRIS/Payroll provider. Please try connecting it again.'
        );
      }
    }
  };

  const checkSyncStatus = (): void => {
    let timeOut = false;
    setTimeout(() => {
      timeOut = true;
    }, 3600000);
    let delay = 120000;

    const getSyncStatusAndSetNewTimeOut = (): void => {
      void (async () => {
        const updatedReport = await getReport(report.id);
        if (updatedReport?.hrisDataSyncing !== true) {
          await importHRISData();
          setShowProcessingModal(false);
        } else if (!timeOut) {
          delay = Math.max(delay / 2, 30000);
          setTimeout(getSyncStatusAndSetNewTimeOut, delay);
        }
      })();
    };
    setTimeout(getSyncStatusAndSetNewTimeOut, delay);
  };

  useEffect(() => {
    void (async () => {
      if (report.hrisDataSyncing === true) {
        setIsLoading(false);
        setShowProcessingModal(true);
        checkSyncStatus();
      } else {
        await importHRISData();
      }

      setUserEmail(await getUserEmail());
    })();
  }, []);

  return (
    <>
      {isLoading ? (
        <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
          <div className="text-center py-16">
            <Spinner className="text-gray-500" size={48} />
          </div>
        </div>
      ) : (
        <>
          {showHRISDataInsights && <HRISDataInsights report={report} />}
          {showProcessingModal && (
            <ProcessingModal
              header={
                <>
                  <div className="text-2xl font-bold mb-8">
                    {report.hrisDataResynced === true
                      ? 'Refreshing data now in progress...'
                      : 'Connection successful! Import now in progress...'}
                  </div>
                  <div className="text-base px-4">
                    Depending on how many employees you have, it will take between{' '}
                    <span className="font-semibold inline">5 minutes to 3 hours</span> to import
                    your data.
                    <br />
                    <br />
                    We’ll send you an email at {userEmail} when your import is complete.
                  </div>
                </>
              }
            />
          )}
        </>
      )}
    </>
  );
}

export default withReport(ImportingHRISDataPage);
